import type {
  NetworkSandbox,
  NetworkSandboxOptions,
  CreateNetworkSandbox,
} from './network-sandbox.types.js'

/**
 * isRelativeUrl — returns true for URLs that start with '/', './', or '../'.
 * Used to detect app fetch calls that should be rewritten to the app's apiBaseUrl.
 */
function isRelativeUrl(url: string): boolean {
  return url.startsWith('/') || url.startsWith('./') || url.startsWith('../')
}

/**
 * resolveRelativeUrl — prepends apiBaseUrl to a relative URL.
 *
 * Simplified resolution — covers the three relative patterns without full
 * URL resolution (sufficient for sandbox purposes since app-runtime.ts uses
 * string URLs at the PlatformAdapter.fetch call site).
 *
 * @param url Relative URL (starting with '/', './', or '../')
 * @param base apiBaseUrl (e.g. 'https://api.myapp.com')
 */
function resolveRelativeUrl(url: string, base: string): string {
  // Strip trailing slash from base to avoid double slashes
  const cleanBase = base.replace(/\/$/, '')
  if (url.startsWith('/')) {
    return cleanBase + url
  }
  if (url.startsWith('./')) {
    return cleanBase + '/' + url.slice(2)
  }
  if (url.startsWith('../')) {
    return cleanBase + '/' + url.slice(3)
  }
  return cleanBase + '/' + url
}

/**
 * createNetworkSandbox — factory for a fetch interceptor sandbox.
 *
 * Provides two protections:
 * 1. Relative URL rewriting — app fetch('/api/x') → fetch('https://api.myapp.com/api/x')
 * 2. Credential downgrade — credentials:'include' → credentials:'same-origin' in edit mode
 *
 * NetworkSandbox is STATELESS. destroy() is a no-op.
 *
 * Design: Phase 151 TIMER-NETWORK-SANDBOX-DESIGN.md
 * Interface: network-sandbox.types.ts
 */
export const createNetworkSandbox: CreateNetworkSandbox = (options?: NetworkSandboxOptions): NetworkSandbox => {
  const stripCredentials = options?.stripCredentialsInEditMode ?? true
  const baseFetch = options?.baseFetch ?? globalThis.fetch

  return {
    fetch(input, init?) {
      // Step 1: Extract original URL string
      let originalUrl: string
      if (input instanceof Request) {
        originalUrl = input.url
      } else if (input instanceof URL) {
        originalUrl = input.href
      } else {
        originalUrl = String(input)
      }

      // Step 2: Rewrite URL if relative and apiBaseUrl is set
      let rewrittenUrl: string | null = null
      let finalInput: RequestInfo | URL = input
      if (options?.apiBaseUrl && isRelativeUrl(originalUrl)) {
        rewrittenUrl = resolveRelativeUrl(originalUrl, options.apiBaseUrl)
        finalInput = rewrittenUrl
      }

      // Step 3: Downgrade credentials if needed
      let credentialsAction: 'none' | 'downgraded' | 'blocked' = 'none'
      let finalInit = init
      if (stripCredentials && init?.credentials === 'include') {
        finalInit = { ...init, credentials: 'same-origin' }
        credentialsAction = 'downgraded'
      }

      // Step 4: Call onIntercepted ONLY when something was modified
      // (URL rewrite or credential downgrade — NOT on unmodified passthrough)
      if (options?.onIntercepted && (rewrittenUrl !== null || credentialsAction !== 'none')) {
        options.onIntercepted({ originalUrl, rewrittenUrl, credentialsAction })
      }

      // Step 5: Delegate to baseFetch
      return baseFetch(finalInput, finalInit)
    },

    destroy() {
      // Stateless — no persistent state to clean up.
      // Exists for lifecycle consistency with other AppSandbox modules.
    },
  }
}
